import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shift, ShiftPattern, listShiftPatterns, listShifts } from '../services/shiftService';

/**
 * 自分のシフト（従業員向け・スマートフォン想定）。
 *
 * ## 見えるのは自分のぶん・公開済みだけ
 *
 * どちらも**サーバ側で落としている**（`mine: true` を付けると
 * `user_id = 自分 AND published_at IS NOT NULL` になる）。ここで隠しているのではなく、
 * 届いていない。組みかけのシフトが見えると、確定していない予定で動かれるため。
 *
 * ⚠️ **`mine` を外すと権限で範囲が決まってしまう**（代表指摘 2026-09-02）。
 * 店長がこの画面を開くと、他人のシフトが自分のものとして並んだ。
 */

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const MyShiftPage: React.FC = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => ymd(new Date()).slice(0, 7));
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [patterns, setPatterns] = useState<ShiftPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planBlocked, setPlanBlocked] = useState(false);

  const range = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
  }, [month]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [s, p] = await Promise.all([
        listShifts(range.from, range.to, { mine: true }),
        // パターンは略号と色を出すためだけに使う。取れなくても表示は成立させる
        listShiftPatterns().catch(() => [] as ShiftPattern[]),
      ]);
      setShifts(s.shifts);
      setPatterns(p);
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || 'シフトの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  /** 日付ごとにまとめる。1日に複数の枠（中抜け）があるため配列で持つ */
  const byDate = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const sh of shifts) {
      const d = String(sh.date).slice(0, 10);
      if (!map.has(d)) map.set(d, []);
      map.get(d)!.push(sh);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [shifts]);

  if (planBlocked) {
    return (
      <div style={s.root}>
        <div style={s.card}>
          <h1 style={s.title}>シフト</h1>
          <p style={s.note}>この会社ではシフト管理をご利用いただけません。</p>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>ダッシュボードに戻る</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.root}>
      <div style={s.card}>
        <h1 style={s.title}>自分のシフト</h1>

        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={s.input} />

        {error && <p style={s.error}>{error}</p>}
        {loading && <p style={s.note}>読み込み中…</p>}

        {!loading && byDate.length === 0 && (
          // 「無い」のか「まだ公開されていない」のかを区別して伝える
          <p style={s.note}>
            この月の公開されたシフトはまだありません。
            <br />
            公開されると、ここに出ます。
          </p>
        )}

        <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
          {byDate.map(([date, list]) => {
            const w = WEEK[new Date(`${date}T00:00:00`).getDay()];
            const isDayOff = list.some((x) => x.isDayOff);
            return (
              <div key={date} style={s.row}>
                <div style={{ minWidth: 68 }}>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>{Number(date.slice(8))}日</div>
                  <div style={{ fontSize: 12, color: w === '日' ? '#DC2626' : w === '土' ? '#2563EB' : '#64748B' }}>
                    ({w})
                  </div>
                </div>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  {isDayOff ? (
                    <span style={{ color: '#64748B', fontWeight: 700 }}>公休</span>
                  ) : (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {list.map((x) => {
                        const p = patterns.find((pp) => pp.id === x.patternId);
                        return (
                          <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {p && (
                              <span style={{ background: p.color || '#2563EB', color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>
                                {p.shortLabel}
                              </span>
                            )}
                            <span style={{ fontSize: 16, fontWeight: 700 }}>
                              {(x.startTime || '').slice(0, 5)} – {(x.endTime || '').slice(0, 5)}
                            </span>
                            {x.breakMinutes > 0 && (
                              <span style={{ fontSize: 12, color: '#64748B' }}>休憩 {x.breakMinutes}分</span>
                            )}
                          </div>
                        );
                      })}
                      {/* 中抜けは1日に2行出る。並びが分かるようにしておく */}
                      {list.length > 1 && <div style={{ fontSize: 12, color: '#94A3B8' }}>この日は{list.length}回の勤務です</div>}
                    </div>
                  )}
                  {list.map((x) => x.note).filter(Boolean).map((n, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{n}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={() => navigate('/dashboard')} style={{ ...s.backBtn, marginTop: 20 }}>
          ダッシュボードに戻る
        </button>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh', background: '#F1F5F9', padding: 16 },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #dee2e6', padding: 'clamp(16px, 4vw, 28px)', width: 'min(96vw, 620px)', margin: '0 auto' },
  title: { margin: '0 0 16px', fontSize: 'clamp(17px, 4vw, 21px)' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.8, margin: '12px 0 0' },
  error: { color: '#721c24', background: '#f8d7da', borderRadius: 4, padding: 10, fontSize: 14, marginTop: 12 },
  input: { padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  row: { display: 'flex', gap: 14, alignItems: 'flex-start', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 14px' },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
};

export default MyShiftPage;
